// The progress ruleset: per-key memory, completions and the unit-unlock
// cascade. Pure, total, and clock-free — `now` comes from the event, which is
// what lets two thousand events re-fold to the same answer on any device.
//
// Neutral lookups only. The scoring engine has no language: it must never hold
// a Course, and this import is what keeps that true.

import { hasLesson, lessonById, units } from "../content/course.ts";
import type { LessonDef, UnitDef } from "../content/types.ts";
import { itemIdOf } from "../content/types.ts";
import type { ProgressKey } from "../content/types.ts";
import { KIND_RULES, passed } from "../lessons.ts";
import type { CardState, GameState } from "../state.ts";
import { stampWall } from "./clock.ts";
import type { Grading, PrefKey, PrefPatch, Outcome, ProgressEvent } from "./event.ts";
import { isMemory, review } from "./memory.ts";
import { finished } from "./query.ts";

export type LessonEvent = Extract<ProgressEvent, { kind: "lesson" }>;

export interface LessonSummary {
	/** Units unlocked by finishing it. */
	newly: string[];
	/** The lesson just finished, or null for a practice round or a review of given keys. */
	lesson: LessonDef | null;
	/** The unit practised, or null for a practice round or a review of given keys. */
	unit: UnitDef | null;
	/** Whether a pass-marked lesson (a checkpoint) passed; null for any other. */
	passed: boolean | null;
	/** Items answered wrong at least once, unique, in first-miss order. */
	missed: string[];
	/** The same misses as progress keys, unique, in first-miss order — the exact
	 * input to the result screen's "Review these". A letter missed in two skills
	 * is two keys here and one item in `missed`. */
	missedKeys: ProgressKey[];
	mistakes: number;
	firstTry: number;
	graded: number;
}

export type Merger<T> = (prev: T, patch: T | undefined) => T;

const lastWriteWins = <T>(prev: T, patch: T | undefined): T => patch ?? prev;

/** Onboarding versions only move forward, whichever device reports last. */
const highest: Merger<number> = (prev, patch) =>
	patch === undefined ? prev : Math.max(prev, patch);

/** A hint once shown stays shown, whichever device reports last. */
const either: Merger<boolean> = (prev, patch) => prev || patch === true;

/**
 * How each device preference merges — the only place anything merges at all;
 * progress does not merge, it folds. A choice a person makes (language, layout)
 * is last-write-wins; a version that marks something seen only moves forward.
 *
 * A mapped type over PrefKey rather than a hand-written function, and PrefKey
 * is `Exclude<keyof GameState, FoldedKey>` rather than a hand-written union.
 * Together that makes adding a GameState field a *classification* decision
 * rather than a silent one: it lands in FoldedKey and the reducer learns it,
 * or it lands here as a merger. (event.ts's FoldedKey comment has the caveat:
 * a field already fails `tsc` today for the duller reason that storage.ts's
 * defaultState() returns a GameState literal, so what this buys is the
 * decision about behaviour, not the alarm that something changed.)
 */
export const PREF: { [K in PrefKey]: Merger<GameState[K]> } = {
	lang: lastWriteWins,
	onboarded: highest,
	layout: lastWriteWins,
	kbdHinted: either,
	treeName: lastWriteWins,
};

// Derived from PREF rather than written out, so the list cannot drift from the
// table it describes.
// `as PrefKey[]` is safe because PREF's own type is `{ [K in PrefKey]: ... }`
// — a mapped type over PrefKey — so only an object literal with exactly
// PrefKey's keys can be assigned to it, and Object.keys can only be reading
// that literal back.
const PREF_KEYS = Object.keys(PREF) as PrefKey[];

function applyPref(state: GameState, patch: PrefPatch): GameState {
	const next = { ...state };
	for (const k of PREF_KEYS) {
		// Each merger is Merger<GameState[K]> for its own K, but the loop erases
		// that correlation — TypeScript cannot see that `PREF[k]`, `state[k]` and
		// `next[k]` share one K. The casts are safe because PREF's mapped type is
		// what pairs the key with its merger; nothing else can populate PREF.
		const merge = PREF[k] as Merger<unknown>;
		(next as Record<string, unknown>)[k] = merge(state[k], patch[k]);
	}
	return next;
}

/**
 * One FSRS review per outcome, in order, at the event's wall time. Several
 * outcomes for one key in one lesson are FSRS short-term steps.
 *
 * Defensive about its argument for the same reason as before: fold() is
 * handed rows written by other builds, so it skips what it cannot read
 * rather than throwing. A stored value that is not a Memory — the retired
 * `{ strength }` shape — is treated as never reviewed.
 */
function applyOutcomes(
	stats: Record<ProgressKey, CardState>,
	outcomes: readonly Outcome[],
	at: number,
): Record<ProgressKey, CardState> {
	if (!Array.isArray(outcomes) || !outcomes.length) return stats;
	const next = { ...stats };
	for (const o of outcomes) {
		// Typed Outcome but possibly not one: this function exists to defend
		// against exactly that, so read through a widened type.
		const raw = o as Partial<Outcome> | null | undefined;
		if (typeof raw?.key !== "string" || typeof raw.right !== "boolean") continue;
		// `via` no longer weights the review, but an unknown grading rule still
		// marks a row this build does not understand — skip it.
		if (raw.via !== "check" && raw.via !== "score") continue;
		const prev: unknown = next[raw.key];
		next[raw.key] = review(isMemory(prev) ? prev : undefined, raw.right, at);
	}
	return next;
}

/**
 * A lesson's verdict on the recent-mistakes list. A lesson re-asks a wrong
 * step until it is right, so the last answer is almost always right; what
 * counts is whether the key went wrong at all in this lesson. Fold order, not
 * wall time, decides which lesson came later, so clock skew between devices
 * cannot reopen a fixed key or close an open one.
 */
function applySlips(
	slips: Record<ProgressKey, number>,
	outcomes: readonly Outcome[],
	at: number,
): Record<ProgressKey, number> {
	const asked = new Set<ProgressKey>();
	const missed = new Set<ProgressKey>();
	for (const o of outcomes) {
		// Same defence as applyOutcomes: rows from other builds may not be Outcomes.
		const raw = o as Partial<Outcome> | null | undefined;
		if (typeof raw?.key !== "string" || typeof raw.right !== "boolean") continue;
		if (raw.via !== "check" && raw.via !== "score") continue;
		asked.add(raw.key);
		if (!raw.right) missed.add(raw.key);
	}
	if (!asked.size) return slips;
	const next: Record<ProgressKey, number> = {};
	for (const [k, t] of Object.entries(slips)) if (!asked.has(k)) next[k] = t;
	for (const k of missed) next[k] = at;
	return next;
}

/**
 * The unit-unlock cascade: a unit opens when the one before it is finished.
 * Completion, not mastery — mastery fades (D1), and a gate on a fading value
 * is the stall this design exists to remove. Mutates its own copy as it goes,
 * so finishing one unit can open a chain on re-fold.
 */
function cascade(next: GameState): Record<string, boolean> {
	const unlocked = { ...next.unlocked };
	units.forEach((x, idx) => {
		const prev = units[idx - 1];
		if (prev && !unlocked[x.id] && unlocked[prev.id] && finished(prev, next)) unlocked[x.id] = true;
	});
	return unlocked;
}

function applyLesson(state: GameState, e: LessonEvent): GameState {
	// An event naming a lesson this build's content does not carry is
	// skipped, not thrown on. The moment content ships independently of the app,
	// a client holds exactly such events, and lessonById's
	// deliberate throw would brick the app for that learner until content rolls
	// forward. Skipping costs one lesson's worth of progress on an old build
	// and nothing at all once the bundle catches up.
	if (e.lessonId !== null && !hasLesson(e.lessonId)) return state;
	// A stamp we cannot read gives no time, and every review needs one. Also
	// a tail corrupted in localStorage, which is the likelier source.
	const wall = stampWall(e.at);
	if (wall < 0) return state;
	// Outcomes must be an array; a null or other type cannot be processed. Skip
	// the event entirely rather than trying to salvage it.
	if (!Array.isArray(e.outcomes)) return state;

	const next: GameState = {
		...state,
		stats: applyOutcomes(state.stats, e.outcomes, wall),
		slips: applySlips(state.slips, e.outcomes, wall),
	};
	if (e.lessonId !== null) {
		const { lesson, unit } = lessonById(e.lessonId);
		if (passed(lesson.kind, e.firstTry, e.graded))
			next.done = { ...state.done, [e.lessonId]: (state.done[e.lessonId] ?? 0) + 1 };
		// A lesson can only have been played in a unit that was open, so the
		// event itself is proof. This is what keeps a re-fold under new rules
		// from re-locking a unit a learner already reached.
		next.unlocked = { ...state.unlocked, [unit.id]: true };
	}
	next.unlocked = cascade(next);
	return next;
}

/**
 * The reducer. Total, pure, clock-free.
 *
 * Exhaustive over ProgressEvent's kinds, so a new kind is a compile error
 * here. An event it cannot interpret returns `state` unchanged — never throws,
 * never guesses.
 */
export function applyEvent(state: GameState, e: ProgressEvent): GameState {
	switch (e.kind) {
		case "lesson":
			return applyLesson(state, e);
		case "pref":
			return applyPref(state, e.patch);
	}
}

/**
 * The result screen's view of one live lesson. Called once, at the end of a
 * lesson the learner just played — never while folding a history.
 *
 * Takes the before and after states rather than recomputing anything: `newly`
 * is what the cascade actually opened. A summary that recomputed it could disagree with the
 * state the learner is looking at.
 */
export function summarise(before: GameState, after: GameState, e: LessonEvent): LessonSummary {
	const found = e.lessonId !== null && hasLesson(e.lessonId) ? lessonById(e.lessonId) : null;
	const unit = found?.unit ?? null;
	// `after.unlocked` is `{...before.unlocked}` with new ids appended in course
	// order by cascade(), and a plain object preserves string-key insertion
	// order — so this filter yields course order, as the old `newly` array did.
	const newly = Object.keys(after.unlocked).filter((id) => !before.unlocked[id]);
	// itemIdOf slices a string, so a row this build cannot read is dropped
	// rather than thrown on.
	const wrong = e.outcomes
		.filter((o) => !o.right && typeof (o as Partial<Outcome>).key === "string")
		.map((o) => o.key);
	return {
		newly,
		lesson: found?.lesson ?? null,
		unit,
		passed:
			found && KIND_RULES[found.lesson.kind].passMark !== undefined
				? passed(found.lesson.kind, e.firstTry, e.graded)
				: null,
		missed: [...new Set(wrong.map(itemIdOf))],
		missedKeys: [...new Set(wrong)],
		mistakes: e.mistakes,
		firstTry: e.firstTry,
		graded: e.graded,
	};
}
